import api from "../_app/api";
import { Query } from "../filter/types";
import { BillDetail, CostBreakdown, GetStatementsResponse, Price, UnbilledCost } from "./types";

export const getUnbilledTotals = async (accountId: string) => {
  const { data }: { data: number } = await api({
    method: "GET",
    url: "/costs/unbilled/net",
    params: { "account-id": accountId },
  });
  return data;
};

export const getLastBill = async (accountId: string) => {
  const { data }: { data: BillDetail } = await api({
    method: "GET",
    url: "/bills/last",
    params: { "account-id": accountId },
  });
  return data;
};

export const getCostsBreakdown = async (accountId: string, period: string, systemInvoiceRef: string) => {
  const { data }: { data: CostBreakdown } = await api({
    method: "GET",
    url: `/costs/${period}/breakdown`,
    params: {
      "account-id": accountId,
      "system-invoice-ref": systemInvoiceRef,
    },
  });
  return data;
};

export const getHeadAccountBillDetails = async (accountId: string, period: string) => {
  const { data }: { data: BillDetail[] } = await api({
    method: "GET",
    url: `/bills/${period}`,
    params: { "account-id": accountId },
  });
  return data;
};

export const getSubAccountBillDetails = async (accountId: string, period: string) => {
  const { data }: { data: BillDetail[] } = await api({
    method: "GET",
    url: `/costs/${period}`,
    params: { "account-id": accountId },
  });
  return data;
};

export const getBillFile = async (accountId: string, systemInvoiceRef: string, type: string) => {
  const resp = await api({
    method: "GET",
    url: `/bills/${systemInvoiceRef}/outputs/${type}`,
    responseType: "arraybuffer",
    params: {
      "account-id": accountId,
      "system-invoice-ref": systemInvoiceRef,
    },
  });
  return resp;
};

export const getBilledPeriods = async (accountId: string) => {
  const { data }: { data: string[] } = await api({
    method: "GET",
    url: "/billed-periods",
    params: { "account-id": accountId },
  });
  return data;
};

export const getUnbilledCosts = async (accountId: string) => {
  const { data }: { data: UnbilledCost[] } = await api({
    method: "GET",
    url: "/costs/unbilled",
    params: { "account-id": accountId },
  });
  return data;
};

export const getUsageFile = async (accountId: string, params: { [x: string]: string }) => {
  const resp = await api({
    method: "GET",
    url: "/usage/files/CSV",
    responseType: "arraybuffer",
    params: {
      ...params,
      "account-id": accountId,
    },
  });
  return resp;
};

export const getInvoiceNumbers = async (accountId: string) => {
  const { data }: { data: any } = await api({
    method: "GET",
    url: "/billing/invoice-numbers",
    params: { "account-id": accountId },
  });
  return data;
};

export const getLastBillPdf = async (accountId: string, systemInvoiceRef: string) => {
  const response = await api({
    method: "GET",
    url: `/bills/${systemInvoiceRef}/outputs/INVOICE`,
    responseType: "arraybuffer",
    params: { "account-id": accountId },
  });
  return response;
};

export const getBalance = async (accountId: string) => {
  const { data }: { data: Price } = await api({
    method: "GET",
    url: "/billing/balance",
    params: { "account-id": accountId },
  });
  return data;
};

export const getOverdue = async (accountId: string) => {
  const { data }: { data: Price } = await api({
    method: "GET",
    url: "/billing/overdue",
    params: { "account-id": accountId },
  });
  return data;
};

export const getStatements = async (accountId: string, startingToken: string, limit: number, queries: Query[]) => {
  let { data }: { data: GetStatementsResponse } = await api({
    method: "GET",
    url: "/statements",
    serialize: true,
    params: {
      "account-id": accountId,
      "starting-token": startingToken,
      size: limit,
      queries,
    },
  });
  return data;
};

export const getStatementsFile = async (accountId: string, ref: string, params: any = {}) => {
  const { data } = await api({
    method: "GET",
    url: `/statements/${ref}/download`,
    params: { ...params, "account-id": accountId },
    responseType: "arraybuffer",
  });
  return data;
};

export const getBulkStatementsFile = async (accountId: string, queries: Query[]) => {
  const params = new URLSearchParams();
  params.append("account-id", accountId);
  queries.forEach((q) => params.append(q.id, q.value));
  const { data } = await api({
    method: "GET",
    url: "/statements/download",
    params,
    responseType: "arraybuffer",
  });
  return data;
};

export const getInvoiceFile = async (accountId: string, params: any = {}) => {
  const { data } = await api({
    method: "GET",
    url: "/statements/invoice/download",
    params: { ...params, "account-id": accountId },
    responseType: "arraybuffer",
  });
  return data;
};
